/**
 * Web Audio synthesiser for the airport's cues.
 *
 * Every voice is routed through one master gain, so mute and volume are a
 * single ramp rather than a flag each oscillator has to remember. Nothing is
 * created until the first cue or `unlock()`, because browsers refuse to start
 * an `AudioContext` outside a user gesture.
 */

type ContextFactory = () => AudioContext | null;

export interface AudioManagerOptions {
  readonly createContext?: ContextFactory;
  readonly volume?: number;
  readonly enabled?: boolean;
}

/** Shortest ramp that still avoids a click when the gain jumps. */
const RAMP_SECONDS = 0.02;
const FLOOR = 0.0001;
/** The wind changes level slowly enough that traffic never pumps it. */
const AMBIENT_RAMP_SECONDS = 1.4;
const AMBIENT_BUFFER_SECONDS = 2;
/** Wind is mostly rumble; anything above this is hiss. */
const AMBIENT_CUTOFF_HZ = 460;

interface AmbientVoice {
  readonly source: AudioBufferSourceNode;
  readonly gain: GainNode;
}

export interface WindOptions {
  /** Level over an empty sky. */
  readonly quiet: number;
  /** Level once the sky is busy. */
  readonly busy: number;
  /** Airborne count at which `busy` is reached. */
  readonly busyAt: number;
}

/**
 * How loud the wind should be for the traffic on screen.
 *
 * It is there to fill an empty sky, so it steps back as the sky fills up
 * rather than competing with the cues that matter.
 */
export function windLevel(airborne: number, options: WindOptions): number {
  const span = Math.max(1, options.busyAt);
  const t = Math.max(0, Math.min(1, airborne / span));
  return options.quiet + (options.busy - options.quiet) * t;
}

function defaultContextFactory(): AudioContext | null {
  if (typeof window === "undefined") {
    return null;
  }
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  return Ctor ? new Ctor() : null;
}

export class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly createContext: ContextFactory;
  private enabled: boolean;
  private level: number;
  private ambient: AmbientVoice | null = null;
  private ambientLevel = 0;

  constructor(options: AudioManagerOptions = {}) {
    this.createContext = options.createContext ?? defaultContextFactory;
    this.enabled = options.enabled ?? true;
    this.level = clampVolume(options.volume ?? 0.7);
  }

  get volume(): number {
    return this.level;
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /** True once a context exists and is running. */
  get isRunning(): boolean {
    return this.ctx !== null && this.ctx.state === "running";
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.applyGain();
  }

  setVolume(volume: number): void {
    this.level = clampVolume(volume);
    this.applyGain();
  }

  /**
   * Start the context from inside a user gesture.
   *
   * Safari will not let a context leave "suspended" any other way, so the
   * first cue of a session is silent unless this runs on the first tap.
   */
  unlock(): void {
    this.context();
  }

  playButtonClick(): void {
    this.tone({ type: "sine", from: 400, to: 150, peak: 0.08, seconds: 0.05 });
  }

  playCabinChime(): void {
    this.tone({ type: "sine", from: 739.99, peak: 0.15, seconds: 0.6 });
    this.tone({
      type: "sine",
      from: 587.33,
      peak: 0.18,
      seconds: 0.9,
      delay: 0.3,
    });
  }

  /** Rising thirds under a breathy sweep — an aircraft getting away. */
  playTakeoff(): void {
    // The engine underneath the thirds: it swells in as the aircraft rolls,
    // instead of being loudest the instant the clearance is given.
    this.tone({
      type: "sawtooth",
      from: 68,
      to: 190,
      peak: 0.1,
      seconds: 1.05,
      attack: 0.5,
    });
    this.tone({
      type: "triangle",
      from: 220,
      to: 520,
      peak: 0.12,
      seconds: 0.55,
    });
    this.tone({
      type: "sine",
      from: 330,
      to: 660,
      peak: 0.07,
      seconds: 0.45,
      delay: 0.08,
    });
  }

  /**
   * Set the ambient wind level, 0 to silence.
   *
   * The loop is built on the first call that asks for sound and then stays
   * up; levels ramp rather than step, so a busy minute does not pump it.
   */
  setAmbient(level: number): void {
    this.ambientLevel = clampVolume(level);
    if (this.ambientLevel > 0) {
      this.startAmbient();
    }
    this.applyAmbient();
  }

  /** Two soft pips: close, but nothing has happened yet. */
  playNearMiss(): void {
    this.tone({ type: "square", from: 880, peak: 0.05, seconds: 0.09 });
    this.tone({
      type: "square",
      from: 880,
      peak: 0.05,
      seconds: 0.09,
      delay: 0.14,
    });
  }

  /** A quiet blip as an arrival appears on the edge of the world. */
  playSpawn(): void {
    this.tone({ type: "sine", from: 520, to: 640, peak: 0.05, seconds: 0.12 });
  }

  /** Flat refusal — distinct from the click, so a bad drop reads as a no. */
  playReject(): void {
    this.tone({
      type: "sawtooth",
      from: 200,
      to: 120,
      peak: 0.07,
      seconds: 0.2,
    });
  }

  playCollisionAlert(): void {
    this.tone({
      type: "sawtooth",
      from: 880,
      to: 440,
      peak: 0.1,
      seconds: 0.25,
    });
  }

  /** Release the audio hardware; the manager can be used again afterwards. */
  dispose(): void {
    const ctx = this.ctx;
    // The wind loop never stops on its own — closing the context is not
    // enough to guarantee the source is released on every browser.
    this.ambient?.source.stop?.();
    this.ambient = null;
    this.ambientLevel = 0;
    this.ctx = null;
    this.master = null;
    if (ctx && ctx.state !== "closed") {
      void ctx.close?.();
    }
  }

  /**
   * Build the wind loop.
   *
   * Brown noise through a lowpass: white noise reads as static, and a set of
   * oscillators reads as a drone. Everything here is optional API on older
   * browsers, so each piece is checked before it is used.
   */
  private startAmbient(): void {
    if (this.ambient || !this.enabled) {
      return;
    }
    const ctx = this.context();
    if (!ctx || !this.master) {
      return;
    }
    if (
      typeof ctx.createBuffer !== "function" ||
      typeof ctx.createBufferSource !== "function" ||
      typeof ctx.createBiquadFilter !== "function" ||
      typeof ctx.sampleRate !== "number"
    ) {
      return;
    }

    const frames = Math.max(
      1,
      Math.floor(ctx.sampleRate * AMBIENT_BUFFER_SECONDS),
    );
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const channel = buffer.getChannelData(0);
    // A tiny LCG rather than Math.random: the loop sounds the same every run,
    // which makes a rendering or audio regression reproducible.
    let seed = 0x2f6e2b1;
    let brown = 0;
    for (let i = 0; i < frames; i++) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      brown = (brown + white * 0.02) / 1.02;
      channel[i] = Math.max(-1, Math.min(1, brown * 3.4));
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime?.(AMBIENT_CUTOFF_HZ, ctx.currentTime);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime?.(0, ctx.currentTime);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    source.start?.(ctx.currentTime);

    this.ambient = { source, gain };
  }

  private applyAmbient(): void {
    if (!this.ctx || !this.ambient) {
      return;
    }
    const now = this.ctx.currentTime;
    const gain = this.ambient.gain.gain;
    gain.cancelScheduledValues?.(now);
    gain.setValueAtTime?.(gain.value, now);
    gain.linearRampToValueAtTime?.(
      this.ambientLevel,
      now + AMBIENT_RAMP_SECONDS,
    );
  }

  private context(): AudioContext | null {
    if (!this.ctx) {
      const ctx = this.createContext();
      if (!ctx) {
        return null;
      }
      this.ctx = ctx;
      const master = ctx.createGain();
      master.gain.setValueAtTime(this.targetGain(), ctx.currentTime);
      master.connect(ctx.destination);
      this.master = master;
    }
    if (this.ctx.state === "suspended") {
      void this.ctx.resume?.();
    }
    return this.ctx;
  }

  private targetGain(): number {
    return this.enabled ? this.level : 0;
  }

  private applyGain(): void {
    if (!this.ctx || !this.master) {
      return;
    }
    const now = this.ctx.currentTime;
    const gain = this.master.gain;
    gain.cancelScheduledValues?.(now);
    gain.setValueAtTime?.(gain.value, now);
    gain.linearRampToValueAtTime?.(this.targetGain(), now + RAMP_SECONDS);
  }

  /** One oscillator voice, enveloped and routed through the master gain. */
  private tone({
    type,
    from,
    to,
    peak,
    seconds,
    delay = 0,
    attack = 0,
  }: {
    type: OscillatorType;
    from: number;
    to?: number;
    peak: number;
    seconds: number;
    delay?: number;
    /** Seconds spent swelling to `peak`; 0 starts there and decays. */
    attack?: number;
  }): void {
    if (!this.enabled) {
      return;
    }
    const ctx = this.context();
    if (!ctx || !this.master) {
      return;
    }

    const start = ctx.currentTime + delay;
    const oscillator = ctx.createOscillator();
    const envelope = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, start);
    if (to !== undefined) {
      oscillator.frequency.exponentialRampToValueAtTime(
        Math.max(FLOOR, to),
        start + seconds,
      );
    }

    // An exponential ramp cannot start from zero, so a swell is a linear ramp
    // up from the floor and an exponential decay from the peak.
    const rise = Math.max(0, Math.min(attack, seconds));
    if (rise > 0) {
      envelope.gain.setValueAtTime(FLOOR, start);
      envelope.gain.linearRampToValueAtTime(peak, start + rise);
    } else {
      envelope.gain.setValueAtTime(peak, start);
    }
    envelope.gain.exponentialRampToValueAtTime(FLOOR, start + seconds);

    oscillator.connect(envelope);
    envelope.connect(this.master);
    oscillator.start(start);
    oscillator.stop(start + seconds);
  }
}

function clampVolume(volume: number): number {
  if (!Number.isFinite(volume)) {
    return 0;
  }
  return Math.max(0, Math.min(1, volume));
}
